/**
 * VÉRAULT · Settings → Notifications panel (Wave 11q)
 *
 * Surfaces:
 *   • Sound on/off
 *   • Browser notifications on/off
 *   • Quiet Hours window (HH:MM start/end, supports crossing midnight)
 *   • Test chime button
 *
 * Per-conversation mute lives on the conversation header, not here.
 *
 * Mounted at /portal/advisor/settings/notifications and
 * /portal/client/settings/notifications. Shared component.
 */
import React from "react";
import { Volume2, VolumeX, BellRing, Bell, Moon, Play } from "lucide-react";
import {
  useNotificationPrefs,
  updateNotificationPrefs,
  playChime,
  primeAudio,
} from "../components/conciergeRealtime";

const Toggle = ({ checked, onChange, dataTestid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    role="switch"
    aria-checked={checked}
    data-testid={dataTestid}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? "bg-[hsl(var(--accent-gold))]" : "bg-[rgba(0,0,0,0.15)]"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

export default function NotificationsSettings() {
  const prefs = useNotificationPrefs();
  const [saving, setSaving] = React.useState(false);
  const [lastSaved, setLastSaved] = React.useState(null);

  const save = async (patch) => {
    setSaving(true);
    await updateNotificationPrefs(patch);
    setLastSaved(Date.now());
    setSaving(false);
  };

  if (!prefs) {
    return <div className="p-8 text-[14px] text-[hsl(var(--foreground-muted))]">Loading…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8 space-y-8" data-testid="notifications-settings">
      <header className="space-y-1.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
          Notifications
        </p>
        <h1 className="font-serif text-[28px] sm:text-[32px] tracking-tight">
          How would you like to hear from us?
        </h1>
        <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))] max-w-prose">
          Quiet, never loud. Your concierge chime can rest at night, and you can mute individual threads from the chat header.
        </p>
      </header>

      {/* Sound */}
      <Row
        icon={prefs.sound_enabled ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
        title="Concierge chime"
        hint="A soft airline seatbelt-release bell when a new message arrives."
      >
        <Toggle
          checked={!!prefs.sound_enabled}
          onChange={(v) => save({ sound_enabled: v })}
          dataTestid="notif-sound-toggle"
        />
      </Row>

      {/* Browser notification */}
      <Row
        icon={prefs.browser_enabled ? <BellRing className="size-4" /> : <Bell className="size-4" />}
        title="Browser notifications"
        hint="Show a system notification when the tab is in the background."
      >
        <Toggle
          checked={!!prefs.browser_enabled}
          onChange={(v) => save({ browser_enabled: v })}
          dataTestid="notif-browser-toggle"
        />
      </Row>

      {/* Quiet Hours */}
      <div className="border border-[rgba(0,0,0,0.06)] rounded-lg p-5 bg-[hsl(var(--background-paper))]/50 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Moon className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]">
                Quiet Hours
              </p>
            </div>
            <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] leading-relaxed">
              Silence chimes + browser notifications between these hours. Times are LOCAL to you. Crossing midnight is supported.
            </p>
          </div>
          <Toggle
            checked={!!prefs.quiet_hours_enabled}
            onChange={(v) => save({ quiet_hours_enabled: v })}
            dataTestid="notif-quiet-hours-toggle"
          />
        </div>
        {prefs.quiet_hours_enabled && (
          <div className="grid grid-cols-2 gap-3 pt-1" data-testid="notif-quiet-hours-range">
            <div>
              <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mb-1">From</span>
              <input
                type="time"
                value={prefs.quiet_hours_start || "22:00"}
                onChange={(e) => save({ quiet_hours_start: e.target.value })}
                className="w-full px-3 py-2 text-[14px] border border-[rgba(0,0,0,0.1)] rounded outline-none focus:border-[hsl(var(--accent-gold))]"
                data-testid="notif-quiet-hours-start"
              />
            </div>
            <div>
              <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mb-1">Until</span>
              <input
                type="time"
                value={prefs.quiet_hours_end || "07:00"}
                onChange={(e) => save({ quiet_hours_end: e.target.value })}
                className="w-full px-3 py-2 text-[14px] border border-[rgba(0,0,0,0.1)] rounded outline-none focus:border-[hsl(var(--accent-gold))]"
                data-testid="notif-quiet-hours-end"
              />
            </div>
          </div>
        )}
      </div>

      {/* Test */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={() => { primeAudio(); playChime(); }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] hover:bg-[hsl(var(--background-paper))] transition-colors"
          data-testid="notif-test-chime"
        >
          <Play className="size-3.5" strokeWidth={1.5} />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            Test the chime
          </span>
        </button>
        {saving ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Saving…</span>
        ) : lastSaved ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">Saved</span>
        ) : null}
      </div>

      {/* Muted conversations summary */}
      {Array.isArray(prefs.muted_conversations) && prefs.muted_conversations.length > 0 && (
        <div className="border-t border-[rgba(0,0,0,0.06)] pt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
            Muted conversations · {prefs.muted_conversations.length}
          </p>
          <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            You've muted {prefs.muted_conversations.length} thread{prefs.muted_conversations.length === 1 ? "" : "s"}. Unmute from each conversation's header.
          </p>
        </div>
      )}
    </div>
  );
}

function Row({ icon, title, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-[rgba(0,0,0,0.06)]">
      <div className="flex-1 min-w-0 flex items-start gap-3">
        <span className="mt-0.5 text-[hsl(var(--accent-gold))]">{icon}</span>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]">{title}</p>
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] leading-relaxed mt-0.5">{hint}</p>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
